import { getRhumbLineBearing } from "geolib";
import type { CSSProperties } from "react";

import {
  NO_VALUE_DASH,
  type IRideInvestigationAnomalyDto,
  type IRideInvestigationDto,
  type IRideInvestigationEventDto,
  type IRideInvestigationPassengerDto,
  type ITrackingRenderState,
  type TLatLngExpression,
  type TLatLngTuple,
} from "@ryde/shared";
import { greenMui, greyMui, orangeMui } from "@ryde/theme";

import {
  CHART_PLOT_HEIGHT,
  CHART_TOP,
  COORDS_PER_POINT,
  DECIMAL_BASE,
  FOLLOW_SAFE_INSET_RATIO,
  GPS_GAP_THRESHOLD_MS,
  INVESTIGATION_MARKER_SIZE,
  INVESTIGATION_PANEL_MAX_WIDTH,
  INVESTIGATION_PANEL_MIN_WIDTH,
  MARKER_ROTATE_CLASS,
  MIDPOINT_DIVISOR,
  MS_PER_MINUTE,
  MS_PER_SECOND,
  PERCENT,
  SECONDS_PER_HOUR,
  SECONDS_PER_MINUTE,
  SVG_COORD_DECIMALS,
  TIME_PAD_LENGTH,
} from "./investigation.constants";
import type {
  IPlaybackEvent,
  IPlaybackModel,
  IPlaybackOccupancyPoint,
  IPlaybackSnapshot,
  IPlaybackStation,
  ITracingRow,
  ITrailDot,
  ITrailSegment,
  TInvestigationSwatch,
  TInvestigationTab,
  TTrailSegmentKind,
} from "./investigation.models";

/**
 * The tab a ride opens on. Without GPS there is no drive to follow, so the History timeline — what did
 * happen to the ride — leads, when the user may read it.
 */
export const resolveOpeningTab = ({
  hasGps,
  canReadHistory,
}: {
  hasGps: boolean;
  canReadHistory: boolean;
}): TInvestigationTab => (!hasGps && canReadHistory ? "history" : "summary");

/** Epoch ms of an ISO timestamp, or NaN when it is missing or unparsable. */
export const parseMs = (iso?: string | null): number => {
  if (!iso) {
    return NaN;
  }

  const ms = Date.parse(iso);

  return Number.isFinite(ms) ? ms : NaN;
};

const lerp = ({
  start,
  end,
  fraction,
}: {
  start: number;
  end: number;
  fraction: number;
}): number => start + (end - start) * fraction;

/** Clamp a value into the 0..1 range. */
export const clamp01 = (value: number): number =>
  Math.min(1, Math.max(0, value));

const pointAt = (latLngs: Float32Array, index: number): TLatLngTuple => [
  latLngs[index * COORDS_PER_POINT],
  latLngs[index * COORDS_PER_POINT + 1],
];

const bearingBetween = (from: TLatLngTuple, to: TLatLngTuple): number => {
  if (from[0] === to[0] && from[1] === to[1]) {
    return 0;
  }

  return getRhumbLineBearing(
    { latitude: from[0], longitude: from[1] },
    { latitude: to[0], longitude: to[1] },
  );
};

/** Rightmost index in the ascending `times` array whose value is <= `value` (or -1). */
const lastIndexLe = ({
  times,
  value,
  length,
}: {
  times: Float64Array;
  value: number;
  length: number;
}): number => {
  let low = 0;
  let high = length - 1;
  let result = -1;

  while (low <= high) {
    const mid = Math.floor((low + high) / MIDPOINT_DIVISOR);

    if (times[mid] <= value) {
      result = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  return result;
};

const lastOccupancyIndexLe = (
  points: IPlaybackOccupancyPoint[],
  value: number,
): number => {
  let low = 0;
  let high = points.length - 1;
  let result = -1;

  while (low <= high) {
    const mid = Math.floor((low + high) / MIDPOINT_DIVISOR);

    if (points[mid].tMs <= value) {
      result = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  return result;
};

/** Build the preprocessed playback model from the investigation payload and (optionally) the GPS trail. */
export const buildPlaybackModel = ({
  payload,
  renderState,
}: {
  payload: IRideInvestigationDto;
  renderState?: ITrackingRenderState;
}): IPlaybackModel => {
  let pointCount: number;
  let pointTimesMs: Float64Array;
  let latLngs: Float32Array;
  let pointDetour: Uint8Array;
  let speeds: (number | undefined)[];

  if (renderState && renderState.pointCount > 0) {
    pointCount = renderState.pointCount;
    latLngs = renderState.latLngs;
    pointDetour = renderState.pointDetour;
    speeds = renderState.speeds;
    pointTimesMs = Float64Array.from(renderState.timestamps, timestamp =>
      Date.parse(timestamp),
    );
  } else {
    // The real trail has not arrived yet — stand in with a coarse "trail" through the stations so playback can
    // start. A ride the backend says has no GPS gets no stand-in: a path through station times would replay a
    // drive that never happened.
    const stationTrail = (payload.playback.hasGps ? payload.stations : [])
      .map(station => ({
        tMs: parseMs(station.actualArrival ?? station.scheduledArrival),
        lat: station.latitude,
        lng: station.longitude,
      }))
      .filter(
        entry =>
          Number.isFinite(entry.tMs) &&
          entry.lat !== undefined &&
          entry.lng !== undefined,
      )
      .sort((a, b) => a.tMs - b.tMs);

    pointCount = stationTrail.length;
    latLngs = new Float32Array(pointCount * COORDS_PER_POINT);
    pointTimesMs = new Float64Array(pointCount);
    pointDetour = new Uint8Array(pointCount);
    speeds = new Array(pointCount).fill(undefined);
    stationTrail.forEach((entry, index) => {
      latLngs[index * COORDS_PER_POINT] = entry.lat as number;
      latLngs[index * COORDS_PER_POINT + 1] = entry.lng as number;
      pointTimesMs[index] = entry.tMs;
    });
  }

  const stations: IPlaybackStation[] = payload.stations.map(station => {
    const tMs = parseMs(station.actualArrival ?? station.scheduledArrival);

    return {
      station,
      tMs: Number.isFinite(tMs) ? tMs : undefined,
      latLng:
        station.latitude !== undefined && station.longitude !== undefined
          ? ([station.latitude, station.longitude] as TLatLngTuple)
          : undefined,
    };
  });

  const events: IPlaybackEvent[] = payload.events
    .map(event => ({
      event,
      tMs: parseMs(event.timestamp),
      endMs: event.endTimestamp ? parseMs(event.endTimestamp) : undefined,
    }))
    .filter(entry => Number.isFinite(entry.tMs))
    .sort((a, b) => a.tMs - b.tMs);

  const occupancy: IPlaybackOccupancyPoint[] = payload.occupancyTimeline
    .map(point => ({ tMs: parseMs(point.t), count: point.count }))
    .filter(point => Number.isFinite(point.tMs))
    .sort((a, b) => a.tMs - b.tMs);

  let startMs = parseMs(payload.playback.startTime);
  let endMs = parseMs(payload.playback.endTime);

  if (!Number.isFinite(startMs)) {
    startMs =
      pointCount > 0
        ? pointTimesMs[0]
        : (stations.find(station => station.tMs !== undefined)?.tMs ?? 0);
  }

  if (!Number.isFinite(endMs)) {
    endMs =
      pointCount > 0
        ? pointTimesMs[pointCount - 1]
        : stations.reduce(
            (max, station) => Math.max(max, station.tMs ?? max),
            startMs,
          );
  }

  if (endMs <= startMs) {
    endMs = startMs + 1;
  }

  return {
    rideId: payload.summary.rideId,
    startMs,
    endMs,
    durationMs: endMs - startMs,
    hasGps: payload.playback.hasGps,
    pointCount,
    pointTimesMs,
    latLngs,
    pointDetour,
    speeds,
    stations,
    events,
    occupancy,
    payload,
  };
};

/** Sample the vehicle position and derived state at a given playhead time (cheap; safe per animation frame). */
export const sampleAt = (
  model: IPlaybackModel,
  tMs: number,
): IPlaybackSnapshot => {
  const clamped = Math.min(Math.max(tMs, model.startMs), model.endMs);
  const { pointCount, pointTimesMs, latLngs, pointDetour, speeds } = model;

  let latLng: TLatLngTuple | undefined;
  let bearingDeg = 0;
  let speedKmh: number | undefined;
  let isInDetour = false;

  if (pointCount > 0) {
    const index = lastIndexLe({
      times: pointTimesMs,
      value: clamped,
      length: pointCount,
    });

    if (index < 0) {
      latLng = pointAt(latLngs, 0);
      bearingDeg =
        pointCount > 1
          ? bearingBetween(pointAt(latLngs, 0), pointAt(latLngs, 1))
          : 0;
      speedKmh = speeds[0];
      isInDetour = pointDetour[0] === 1;
    } else if (index >= pointCount - 1) {
      const lastIndex = pointCount - 1;

      latLng = pointAt(latLngs, lastIndex);
      bearingDeg =
        pointCount > 1
          ? bearingBetween(
              pointAt(latLngs, lastIndex - 1),
              pointAt(latLngs, lastIndex),
            )
          : 0;
      speedKmh = speeds[lastIndex];
      isInDetour = pointDetour[lastIndex] === 1;
    } else {
      const t0 = pointTimesMs[index];
      const t1 = pointTimesMs[index + 1];
      const fraction = t1 > t0 ? (clamped - t0) / (t1 - t0) : 0;
      const from = pointAt(latLngs, index);
      const to = pointAt(latLngs, index + 1);
      latLng = [
        lerp({ start: from[0], end: to[0], fraction }),
        lerp({ start: from[1], end: to[1], fraction }),
      ];
      bearingDeg = bearingBetween(from, to);
      speedKmh = speeds[index];
      isInDetour = pointDetour[index] === 1 || pointDetour[index + 1] === 1;
    }
  }

  const occupancyIndex = lastOccupancyIndexLe(model.occupancy, clamped);
  const occupancy =
    occupancyIndex >= 0 ? model.occupancy[occupancyIndex].count : 0;

  let currentStationIndex = -1;

  for (let i = 0; i < model.stations.length; i++) {
    const stationTime = model.stations[i].tMs;

    if (stationTime !== undefined && stationTime <= clamped) {
      currentStationIndex = i;
    }
  }

  return {
    tMs: clamped,
    latLng,
    bearingDeg,
    speedKmh,
    inDetour: isInDetour,
    occupancy,
    currentStationIndex,
  };
};

/** Split the GPS trail into runs of the same kind (normal / detour / signal-gap) for layered styling. */
export const buildTrailSegments = (model?: IPlaybackModel): ITrailSegment[] => {
  if (!model || model.pointCount <= 1) {
    return [];
  }

  const kindAt = (pair: number): TTrailSegmentKind => {
    if (
      model.pointTimesMs[pair + 1] - model.pointTimesMs[pair] >
      GPS_GAP_THRESHOLD_MS
    ) {
      return "gap";
    }

    return model.pointDetour[pair] === 1 || model.pointDetour[pair + 1] === 1
      ? "detour"
      : "normal";
  };

  const segments: ITrailSegment[] = [];
  const pushSegment = ({
    startPoint,
    endPoint,
    kind,
  }: {
    startPoint: number;
    endPoint: number;
    kind: TTrailSegmentKind;
  }): void => {
    const positions: TLatLngExpression[] = [];

    for (let point = startPoint; point <= endPoint; point++) {
      positions.push(pointAt(model.latLngs, point));
    }

    segments.push({ positions, kind });
  };

  let runStart = 0;
  let runKind = kindAt(0);

  for (let pair = 1; pair < model.pointCount - 1; pair++) {
    const kind = kindAt(pair);

    if (kind !== runKind) {
      pushSegment({ startPoint: runStart, endPoint: pair, kind: runKind });
      runStart = pair;
      runKind = kind;
    }
  }

  pushSegment({
    startPoint: runStart,
    endPoint: model.pointCount - 1,
    kind: runKind,
  });

  return segments;
};

/** Decimated trail-sample dots (bounded count) carrying the time/speed at each sampled point. */
export const buildTrailDots = (
  model: IPlaybackModel | undefined,
  maxDots: number,
): ITrailDot[] => {
  if (!model || model.pointCount <= 1) {
    return [];
  }

  const step = Math.max(1, Math.ceil(model.pointCount / maxDots));
  const dots: ITrailDot[] = [];

  for (let i = 0; i < model.pointCount; i += step) {
    dots.push({
      position: pointAt(model.latLngs, i),
      tMs: model.pointTimesMs[i],
      speedKmh: model.speeds[i],
    });
  }

  return dots;
};

/** Decimated GPS samples (time / speed / off-route) for the Tracing tab list. */
export const buildTracingRows = (
  model: IPlaybackModel | undefined,
  maxRows: number,
): ITracingRow[] => {
  if (!model || model.pointCount === 0) {
    return [];
  }

  const step = Math.max(1, Math.ceil(model.pointCount / maxRows));
  const rows: ITracingRow[] = [];

  for (let i = 0; i < model.pointCount; i += step) {
    rows.push({
      tMs: model.pointTimesMs[i],
      speedKmh: model.speeds[i],
      inDetour: model.pointDetour[i] === 1,
    });
  }

  return rows;
};

/** Events whose start time falls in (fromMs, toMs] — used to fire "as they happen" callouts while playing. */
export const eventsInRange = ({
  model,
  fromMs,
  toMs,
}: {
  model: IPlaybackModel;
  fromMs: number;
  toMs: number;
}): IPlaybackEvent[] => {
  if (toMs <= fromMs) {
    return [];
  }

  return model.events.filter(entry => entry.tMs > fromMs && entry.tMs <= toMs);
};

/** Fraction [0, 1] of the playback window for a given time. */
export const fractionForTime = (model: IPlaybackModel, tMs: number): number => {
  if (model.durationMs <= 0) {
    return 0;
  }

  return Math.min(1, Math.max(0, (tMs - model.startMs) / model.durationMs));
};

/** Absolute time for a fraction [0, 1] of the playback window. */
export const timeForFraction = (
  model: IPlaybackModel,
  fraction: number,
): number =>
  model.startMs + Math.min(1, Math.max(0, fraction)) * model.durationMs;

/**
 * Playhead time a roster row seeks to, or undefined when the ride never recorded one. Parsed here and
 * not at the call site: an unparseable stamp would otherwise reach `seekToMs` as NaN and leave the
 * playhead — and with it every readout on the panel — stuck.
 */
export const passengerSeekMs = (
  passenger: IRideInvestigationPassengerDto,
): number | undefined => {
  const ms = parseMs(passenger.timestamp);

  return Number.isNaN(ms) ? undefined : ms;
};

/**
 * Whether the vehicle's projected screen position still sits inside the follow "safe box" — the middle of
 * the map, inset by `FOLLOW_SAFE_INSET_RATIO` on each axis. A viewport with no size yet, or a point that
 * could not be projected, counts as inside: the camera must never chase a position it cannot place.
 */
export const isInsideFollowBox = ({
  x,
  y,
  width,
  height,
}: {
  x: number;
  y: number;
  width: number;
  height: number;
}): boolean => {
  if (
    !Number.isFinite(x) ||
    !Number.isFinite(y) ||
    !(width > 0) ||
    !(height > 0)
  ) {
    return true;
  }

  const insetX = width * FOLLOW_SAFE_INSET_RATIO;
  const insetY = height * FOLLOW_SAFE_INSET_RATIO;

  return (
    x >= insetX && x <= width - insetX && y >= insetY && y <= height - insetY
  );
};

/** Clamp the panel width to its resize bounds. */
export const clampPanelWidth = (width: number): number =>
  Math.min(
    INVESTIGATION_PANEL_MAX_WIDTH,
    Math.max(INVESTIGATION_PANEL_MIN_WIDTH, width),
  );

/**
 * Panel width for a resizer drag, from the workspace box and the pointer.
 *
 * The panel column is the workspace's inline END, and the workspace flips with the locale — so under
 * RTL it sits at the LEFT edge, and measuring back from `right` returns the MAP's width instead. That
 * number is far past the maximum at every pointer position the resizer can reach, so the width pinned
 * to the clamp and the split looked frozen. The timeline next to it does not need this: it is held at
 * physical LTR on purpose, so its own pointer math stays direction-free.
 */
export const panelWidthForPointer = ({
  workspaceLeft,
  workspaceRight,
  clientX,
  isRtl,
}: {
  workspaceLeft: number;
  workspaceRight: number;
  clientX: number;
  isRtl: boolean;
}): number =>
  clampPanelWidth(isRtl ? clientX - workspaceLeft : workspaceRight - clientX);

/** CSS border-top style for a top-bar filter line swatch. */
export const lineDashStyle = (
  swatch: TInvestigationSwatch,
): CSSProperties["borderTopStyle"] => {
  if (swatch === "line-dashed") {
    return "dashed";
  }

  if (swatch === "line-dotted") {
    return "dotted";
  }

  return "solid";
};

const pad = (value: number): string =>
  String(value).padStart(TIME_PAD_LENGTH, "0");

const roundTo1 = (value: number): number =>
  Math.round(value * DECIMAL_BASE) / DECIMAL_BASE;

/** Format an elapsed duration (ms) as m:ss, or h:mm:ss past an hour. */
export const formatElapsed = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.round(ms / MS_PER_SECOND));
  const hours = Math.floor(totalSeconds / SECONDS_PER_HOUR);
  const minutes = Math.floor(
    (totalSeconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE,
  );
  const seconds = totalSeconds % SECONDS_PER_MINUTE;

  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(seconds)}`
    : `${minutes}:${pad(seconds)}`;
};

export const formatKm = (km?: number): string =>
  km === undefined || km === null ? NO_VALUE_DASH : `${roundTo1(km)} km`;

export const formatKmh = (kmh?: number): string =>
  kmh === undefined || kmh === null ? NO_VALUE_DASH : `${Math.round(kmh)} km/h`;

/** Human duration from seconds: "8m", "1h 05m". */
export const formatDurationSec = (sec?: number): string => {
  if (sec === undefined || sec === null) {
    return NO_VALUE_DASH;
  }

  const total = Math.max(0, Math.round(sec));
  const hours = Math.floor(total / SECONDS_PER_HOUR);
  const minutes = Math.round((total % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);

  return hours > 0 ? `${hours}h ${pad(minutes)}m` : `${minutes}m`;
};

/** Signed minutes with a leading sign: "+5m", "-2m", "0m". */
export const formatSignedMinutes = (minutes?: number): string => {
  if (minutes === undefined || minutes === null) {
    return NO_VALUE_DASH;
  }

  const rounded = Math.round(minutes);

  return rounded > 0 ? `+${rounded}m` : `${rounded}m`;
};

/** Minutes between two epoch-ms values (signed). */
export const minutesBetweenMs = (fromMs: number, toMs: number): number =>
  roundTo1((toMs - fromMs) / MS_PER_MINUTE);

/** Chart color for a signed delay in minutes (neutral at zero, orange late, green early). */
export const delayColor = (minutes?: number): string => {
  if (minutes === undefined || minutes === 0) {
    return greyMui._600;
  }

  return minutes > 0 ? orangeMui._700 : greenMui._700;
};

/** Short dwell label from seconds ("45s", "3m"); empty when there is no dwell. */
export const dwellLabel = (seconds?: number): string => {
  if (!seconds) {
    return "";
  }

  return seconds < SECONDS_PER_MINUTE
    ? `${Math.round(seconds)}s`
    : `${Math.round(seconds / SECONDS_PER_MINUTE)}m`;
};

/** Parenthetical detail for an anomaly row (count, delay or duration). */
export const anomalyMeta = (anomaly: IRideInvestigationAnomalyDto): string => {
  if (anomaly.meta?.count !== undefined) {
    return ` (${anomaly.meta.count})`;
  }

  if (anomaly.meta?.delayMinutes !== undefined) {
    return ` (${formatSignedMinutes(Number(anomaly.meta.delayMinutes))})`;
  }

  if (anomaly.meta?.durationSec !== undefined) {
    return ` (${formatDurationSec(Number(anomaly.meta.durationSec))})`;
  }

  return "";
};

/** Secondary "· detail" line for an event row (duration / delay / dwell / free-text). */
export const buildEventMeta = (
  event: IRideInvestigationEventDto,
): string | undefined => {
  const parts: string[] = [];

  if (event.meta?.durationSec !== undefined) {
    parts.push(formatDurationSec(Number(event.meta.durationSec)));
  }

  if (event.meta?.delayMinutes !== undefined) {
    parts.push(formatSignedMinutes(Number(event.meta.delayMinutes)));
  }

  if (event.meta?.dwellSeconds !== undefined) {
    parts.push(formatDurationSec(Number(event.meta.dwellSeconds)));
  }

  if (event.detail) {
    parts.push(event.detail);
  }

  return parts.length ? parts.join(" · ") : undefined;
};

/** "· station/passenger" subject suffix for an event label. */
export const eventSubject = (event: IRideInvestigationEventDto): string => {
  if (event.stationName) {
    return ` · ${event.stationName}`;
  }

  if (event.passengerName) {
    return ` · ${event.passengerName}`;
  }

  return "";
};

/** SVG step path of on-board count over the playback window (undefined when empty). */
export const buildOccupancyPath = (
  model: IPlaybackModel,
  peak: number,
): string | undefined => {
  if (!model.occupancy.length) {
    return undefined;
  }

  const yFor = (count: number): number =>
    CHART_TOP + (1 - (peak > 0 ? count / peak : 0)) * CHART_PLOT_HEIGHT;
  let path = `M 0 ${yFor(model.occupancy[0].count).toFixed(SVG_COORD_DECIMALS)}`;

  model.occupancy.forEach(point => {
    const x = clamp01(fractionForTime(model, point.tMs)) * PERCENT;

    path += ` H ${x.toFixed(SVG_COORD_DECIMALS)} V ${yFor(point.count).toFixed(SVG_COORD_DECIMALS)}`;
  });

  return `${path} H ${PERCENT}`;
};

/** SVG line path of GPS speed over the playback window (undefined when under two samples). */
export const buildSpeedPath = (model: IPlaybackModel): string | undefined => {
  const samples: { fraction: number; speed: number }[] = [];
  let maxSpeed = 0;

  for (let i = 0; i < model.pointCount; i++) {
    const speed = model.speeds[i];

    if (typeof speed === "number" && Number.isFinite(speed)) {
      maxSpeed = Math.max(maxSpeed, speed);
      samples.push({
        fraction: clamp01(
          (model.pointTimesMs[i] - model.startMs) / model.durationMs,
        ),
        speed,
      });
    }
  }

  if (samples.length <= 1 || maxSpeed <= 0) {
    return undefined;
  }

  const yFor = (speed: number): number =>
    CHART_TOP + (1 - speed / maxSpeed) * CHART_PLOT_HEIGHT;

  return samples
    .map(
      (sample, index) =>
        `${index === 0 ? "M" : "L"} ${(sample.fraction * PERCENT).toFixed(SVG_COORD_DECIMALS)} ${yFor(sample.speed).toFixed(SVG_COORD_DECIMALS)}`,
    )
    .join(" ");
};

/**
 * A navigation-arrow marker HTML (rotates to heading; colored red while off-route). Shared by the Leaflet
 * div-icon and the MapLibre marker element, so both engines animate the identical vehicle.
 */
export const buildPlaybackMarkerHtml = (
  bearingDeg: number,
  color: string,
): string => `
  <span class="${MARKER_ROTATE_CLASS}" style="display:block;width:${INVESTIGATION_MARKER_SIZE}px;height:${INVESTIGATION_MARKER_SIZE}px;transform:rotate(${bearingDeg}deg);transform-origin:center;transition:transform 0.12s linear;">
    <svg viewBox="0 0 24 24" width="${INVESTIGATION_MARKER_SIZE}" height="${INVESTIGATION_MARKER_SIZE}" style="display:block;filter:drop-shadow(0 1px 2px rgba(0,0,0,0.35));">
      <circle cx="12" cy="12" r="11" fill="rgba(255,255,255,0.92)" stroke="${color}" stroke-width="1.5" />
      <path d="M12 4 L18 19 L12 15.4 L6 19 Z" fill="${color}" />
    </svg>
  </span>`;

/** `[lat, lng]` positions to a GeoJSON LineString feature (MapLibre wants `[lng, lat]`). */
export const toLineFeature = (
  positions: TLatLngTuple[],
): GeoJSON.Feature<GeoJSON.LineString> => ({
  type: "Feature",
  properties: {},
  geometry: {
    type: "LineString",
    coordinates: positions.map(([lat, lng]) => [lng, lat]),
  },
});
